import { useIntl } from 'react-intl';

import { IBudget, IBudgetVersionService, IBudgetVersionServiceItem } from '../../../../models/budgetV2';
import { ITruckerPayPlanData } from '../../../../models/truckerPayPlan';

import { formatNumberToCurrency } from '../../../../utils/stringUtils';

import ConfirmDialog from '../../../dialogs/ConfirmDialog';

import { BudgetServiceChooser } from './components/BudgetServiceChooser';
import { BudgetServicePercentages } from './components/BudgetServicePercentages';
import { BudgetServiceItem } from './components/BudgetServiceItem';
import { BudgetServiceItemSkeleton } from './components/BudgetServiceItemSkeleton';
import { findInitialServiceItem } from './utils/findInitialServiceItem';
import { useBudgetServiceForm } from './hooks/useBudgetServiceForm';

interface IBudgetServiceFormProps {
  budget: IBudget;
  serviceIndex: number;
  truckerPayPlan?: ITruckerPayPlanData | null;
  // service as loaded from the API (edit only); items/services absent from it are new and stay fully editable
  initialService?: IBudgetVersionService;

  setService: (service: IBudgetVersionService) => void;
  onRemoveService: () => void;
  onAddItem: () => void;
  isCreating?: boolean;
}

export const BudgetServiceForm = (props: IBudgetServiceFormProps) => {
  const intl = useIntl();

  const {
    budget, serviceIndex, truckerPayPlan, initialService,
    setService, onRemoveService
  } = props;

  const service = budget.versionData?.services?.[serviceIndex];
  if (!service) return null;

  const {
    loadingServiceItems,
    serviceItems,
    initialServiceItems,
    showConfirmDialog,
    calculatedSubtotal,
    calculatedTruckerPayPlanSubtotal,
    showDiscountColumn,

    setLoadingServiceItems,
    applyServiceItemsChange,
    setServiceItems,
    setShowConfirmDialog,
    handleRemoveService,
  } = useBudgetServiceForm(service, setService, truckerPayPlan, initialService);

  return (
    <div className={`border border-gray-200 rounded-md py-4 mt-3 my-6 my-0`}>
      <div className="mb-4 p-4">
        <div className="flex flex-col flex-row gap-4 w-full items-center">
          <div className="flex flex-col flex-row gap-10 items-start flex-1 min-w-0 flex-wrap">
            {!service.isExternalService && (
              <div className="flex w-full flex-shrink-0 mb-4">
                <BudgetServiceChooser
                  budget={budget}
                  serviceIndex={serviceIndex}
                  hasTruckerPayPlan={!!truckerPayPlan}
                  loadingServiceItems={loadingServiceItems}

                  setService={setService}
                  setLoadingServiceItems={setLoadingServiceItems}
                  setServiceItems={setServiceItems}
                />
              </div>
            )}
            <BudgetServicePercentages
              service={service}
              calculatedSubtotal={calculatedSubtotal}
              calculatedTruckerPayPlanSubtotal={calculatedTruckerPayPlanSubtotal}
              setService={setService}
            />
          </div>
          <div className="flex items-center flex-shrink-0  ml-auto lg:ml-0">
            <button
              type="button"
              className="text-red-500 underline underline-offset-2 flex items-center gap-1 hover:opacity-80 text-size-14 whitespace-nowrap cursor-pointer"
              onClick={handleRemoveService}
              title={intl.formatMessage({ id: 'intl-msg:budgets.removeService' })}
            >
              <img
                src="/assets/images/iconClose.svg"
                alt={intl.formatMessage({ id: 'intl-msg:budgets.removeService' })}
                className="w-7 h-7 flex-shrink-0"
              />
              <span className="text-size-16 mb-2 ml-2">{intl.formatMessage({ id: 'intl-msg:budgets.removeService' })}</span>
            </button>
          </div>

          <ConfirmDialog
            show={showConfirmDialog}
            title={intl.formatMessage({ id: 'intl-msg:budgets.removeService' })}
            body={intl.formatMessage({ id: 'intl-msg:budgets.removeServiceConfirmation' })}
            onClose={() => setShowConfirmDialog(false)}
            onConfirm={() => {
              onRemoveService();
              setShowConfirmDialog(false);
            }}
          />
        </div>
      </div>
      <div className="overflow-x-auto">
        <table
          className="table table-layout-fixed table-column-overflow-hidden table-head-filled mb-3 w-full [&_td]:border-0 [&_th]:border-0 [&_tbody_tr]:border-0 [&_tbody_tr]:border-b-0 [&_thead_tr]:border-0 [&_thead_tr]:border-b-0 [&_tbody_tr_td]:border-0 [&_tbody_tr_td]:border-b-0 [&_thead_tr_th]:border-0 [&_thead_tr_th]:border-b-0"
          style={{ borderCollapse: 'separate', borderSpacing: '0 0' }}
        >
          <thead>
            <tr className="border-0 !border-b-0" style={{ borderBottom: 'none' }}>
              <th className="w-[7%] border-0 !border-b-0" style={{ borderBottom: 'none' }}>
                {intl.formatMessage({ id: 'intl-msg:budgets.ref' })}
              </th>
              <th className="w-[13%] border-0 !border-b-0" style={{ borderBottom: 'none' }}>
                {intl.formatMessage({ id: 'intl-msg:budgets.item' })}
              </th>
              <th className="w-[16%] border-0 !border-b-0" style={{ borderBottom: 'none' }}>
                {intl.formatMessage({ id: 'intl-msg:budgets.description' })}
              </th>
              <th className="w-[10%] border-0 !border-b-0" style={{ borderBottom: 'none' }}>
                {intl.formatMessage({ id: 'intl-msg:budgets.quantity' })}
              </th>
              <th className="w-[12%] border-0 !border-b-0" style={{ borderBottom: 'none' }}>
                {intl.formatMessage({ id: 'intl-msg:budgets.value' })}
              </th>
              <th className="w-[13%] border-0 !border-b-0" style={{ borderBottom: 'none' }}>
                {intl.formatMessage({ id: 'intl-msg:budgets.coverage' })}
              </th>
              <th className="w-[10%] border-0 !border-b-0" style={{ borderBottom: 'none' }}>
                {intl.formatMessage({ id: 'intl-msg:budgets.availableIn' })}
              </th>
              {showDiscountColumn && (
                <th className="w-[10%] border-0 !border-b-0" style={{ borderBottom: 'none' }}>
                  {intl.formatMessage({ id: 'intl-msg:budgets.discount' })}
                </th>
              )}
              <th className="w-[15%] border-0 !border-b-0" style={{ borderBottom: 'none' }}>
                {intl.formatMessage({ id: 'intl-msg:budgets.subtotal' })}
              </th>
              <th className="w-[4%] text-center border-0 !border-b-0" style={{ borderBottom: 'none' }}>
              </th>
            </tr>
          </thead>
          <tbody>
            {loadingServiceItems ? (
              <BudgetServiceItemSkeleton showDiscountColumn={showDiscountColumn} />
            ) : (
              serviceItems.map((item, index) => (
                <BudgetServiceItem
                  key={`item-${item.ref}-${index}`}
                  service={service}
                  serviceItem={item}
                  initialServiceItems={initialServiceItems}
                  isItemInService={(code) => serviceItems.some((other, idx) => idx !== index && other.item === code)}
                  showDiscountColumn={showDiscountColumn}
                  isCreating={props.isCreating}

                  setServiceItem={(updatedItem: IBudgetVersionServiceItem) => applyServiceItemsChange(serviceItems.map((i, idx) => idx === index ? updatedItem : i))}
                  removeServiceItem={() => applyServiceItemsChange(
                    // items that came with the loaded budget are kept and flagged as removed; new ones are just dropped
                    !props.isCreating && findInitialServiceItem(initialServiceItems, item)
                      ? serviceItems.map((i, idx) => idx === index ? { ...i, removed: true, changed: true } : i)
                      : serviceItems.filter((_, idx) => idx !== index)
                  )}
                />
              ))
            )}
          </tbody>
        </table>
      </div>
      {service.observation !== undefined && (
        <div className="flex-1 m-4">
          <div className="font-bold mb-2 text-size-12 text-gray-500">{intl.formatMessage({ id: 'intl-msg:budgets.observation' })}</div>
          <div className={`flex flex-row gap-2 items-start`}>
            <textarea
              className="form-control flex-1"
              rows={1}
              value={service.observation || ''}
              onChange={(e) => setService({ ...service, observation: e.target.value })}
            />
          </div>
        </div>
      )}
      <div className={`flex flex-row justify-between items-end mt-4 pl-4 pr-6`}>
        <div className={`flex flex-row gap-4`}>
          {props.onAddItem && (
            <button
              type="button"
              className={`flex flex-row items-center text-gray-500 hover:text-gray-700 bg-transparent border-none p-0 cursor-pointer mr-4`}
              onClick={props.onAddItem}
            >
              <img
                src="/assets/images/plus.svg"
                alt={intl.formatMessage({ id: 'intl-msg:budgets.addItem' })}
                className="w-5 h-5 flex-shrink-0 mr-2"
              />
              <span>{intl.formatMessage({ id: 'intl-msg:budgets.addItem' })}</span>
            </button>
          )}
        </div>
        <div className='flex flex-col items-end'>
          <div className={`text-size-18 text-gray-500`}>{intl.formatMessage({ id: 'intl-msg:budgets.serviceSubtotal' })}: {formatNumberToCurrency(calculatedSubtotal)}</div>
          <div className={`text-size-18 text-[#5CB85C]`}>{intl.formatMessage({ id: 'intl-msg:budgets.truckerPayPlanSubtotal' })}: {formatNumberToCurrency(calculatedTruckerPayPlanSubtotal)}</div>
        </div>
      </div>
    </div>
  );
};
